'use client';

import { google, ics, office365 } from 'calendar-link';
import clsx from 'clsx';
import { isFuture, isToday, isTomorrow } from 'date-fns';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import slugify from 'react-slugify';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import {
  AddToCalendarIcon,
  EspnIcon,
  FlagIcon,
  InfoIcon,
} from '~/components/Icons';
import { squadUpState } from '~/components/layouts/SquadUp';
import type { Broadcast, IGame, ITeam } from '~/types/api';
import {
  trackAddToCalendar,
  trackBroadcastWatch,
  trackTicketStartCart,
  type GameTrackingData,
} from '~/utils/analytics';
import { getColors, getTeamByColor } from '~/utils/getColors';
import { getLocalDateTime } from '~/utils/getLocalDateTime';
import { getLogo } from '~/utils/getLogo';
import { GameStatus } from './GameStatus';

// Extract common styles into constants
const COMMON_STYLES = {
  scoreText: '-mb-[5px] -mt-1 flex gap-x-1.5 text-[14px]',
  teamName: '-mb-[5px] -mt-1 text-[14px] font-bold tracking-[.08em]',
  linkText:
    '-mt-1 font-roboto text-[14px] font-medium tracking-[.08em] underline underline-offset-4',
  dateText: '-mb-[5px] -mt-1 font-roboto text-[14px] font-bold',
};

// Game status constants
const GAME_STATUS = {
  IN_PROGRESS: 'In Progress',
  SCHEDULED: 'Scheduled',
  CANCELLED: 'Cancelled',
  POSTPONED: 'Postponed',
  DELAYED: 'Delayed',
};

const ISSUE_STATUSES = [
  GAME_STATUS.CANCELLED,
  GAME_STATUS.POSTPONED,
  GAME_STATUS.DELAYED,
];

type CalendarEvent = {
  title: string;
  description?: string;
  location?: string;
  start: string | Date;
  duration?: [number, 'hour' | 'minute' | 'day'];
  end?: string | Date;
  allDay?: boolean;
};

interface GameProps {
  item: IGame | IGame[];
  isLast?: boolean;
  isAllStarCup?: boolean;
}

interface CalendarTooltipProps {
  id: string;
  game: IGame;
}

interface PromoTooltipProps {
  id: string;
  content: string;
}

interface GameActionsProps {
  game: IGame;
  isUpcoming: boolean;
  isAllStarCup?: boolean;
  isDoubleHeader?: boolean;
  hideTicketsLink?: boolean;
}

interface GameDateProps {
  gameDate?: string;
  isUpcoming: boolean;
  today: boolean;
  tomorrow: boolean;
  formattedLocalTime: { date: string; time: string } | null;
}

// Extract tooltip components
const CalendarTooltip = ({ id, game }: CalendarTooltipProps) => {
  if (!game.gameDateIso) {
    return null;
  }

  // Parse the ISO string and convert to local time
  const gameDateTime = DateTime.fromISO(game.gameDateIso).toLocal();

  if (!gameDateTime.isValid) {
    return null;
  }

  const event: CalendarEvent = {
    title: `${game.competitors?.[1]?.name || ''} vs ${game.competitors?.[0]?.name || ''}`,
    description: `${game.competitors?.[1]?.name || ''} vs ${game.competitors?.[0]?.name || ''} at ${game.venue.name || ''}`,
    location: `${game.venue.address.city || ''}, ${game.venue.address.state || ''}`,
    start: gameDateTime.toJSDate(),
    duration: [3, 'hour'],
  };

  return (
    <Tooltip
      id={id}
      place="bottom"
      clickable={true}
      variant="light"
      opacity={1}
      role="tooltip"
      className="calendar-tooltip"
      render={() => (
        <div className="flex flex-col gap-y-4 p-2 text-sm">
          <a
            href={google(event)}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-blue-500"
            onClick={() =>
              trackAddToCalendar(game as unknown as GameTrackingData, 'Google')
            }
          >
            GOOGLE
          </a>
          <a
            href={ics(event)}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-blue-500"
            onClick={() =>
              trackAddToCalendar(game as unknown as GameTrackingData, 'Apple')
            }
          >
            APPLE
          </a>
          <a
            href={office365(event)}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-blue-500"
            onClick={() =>
              trackAddToCalendar(game as unknown as GameTrackingData, 'Office')
            }
          >
            OFFICE
          </a>
        </div>
      )}
    />
  );
};

const PromoTooltip = ({ id, content }: PromoTooltipProps) => (
  <Tooltip
    id={id}
    place="bottom"
    clickable={true}
    variant="light"
    opacity={1}
    role="tooltip"
    className="promo-tooltip"
    render={() => (
      <div>
        <strong>PROMOTION</strong>
        <p className="min-w-[150px]">{content}</p>
        {/* <a href="#">View details</a> */}
      </div>
    )}
  />
);

// Extract game actions component
const GameActions = ({
  game,
  isUpcoming,
  isAllStarCup,
  isDoubleHeader,
  hideTicketsLink,
}: GameActionsProps) => (
  <div className="order-2 flex flex-col items-center justify-center gap-y-5 px-2.5 max-lg:border-t max-lg:bg-black max-lg:text-white lg:order-4 lg:flex">
    {isUpcoming ? (
      <>
        {game?.ticketLink &&
          game.recordStatus === GAME_STATUS.SCHEDULED &&
          !hideTicketsLink && (
            <p className={`${COMMON_STYLES.linkText} max-lg:no-underline`}>
              <a
                className="inline-block max-lg:py-8 max-lg:text-[18px]"
                href={game.ticketLink}
                onClick={(e) => {
                  trackTicketStartCart(game as unknown as GameTrackingData);
                  if (game.ticketLink?.includes('squadup.com')) {
                    e.preventDefault();
                    const parts = game.ticketLink.split('/');
                    const id = parts[parts.length - 1];

                    // Pass event data to squadUpState
                    squadUpState.eventData = {
                      gameId: game.gameId,
                      homeTeam: game.competitors?.find(
                        (c: ITeam) => c.competitorId === game.homeTeamId,
                      ),
                      awayTeam: game.competitors?.find(
                        (c: ITeam) => c.competitorId === game.awayTeamId,
                      ),
                      date: game.gameDateIso,
                      venue: game.venue,
                    };

                    squadUpState.eventId = id;
                  }
                }}
                target="_blank"
              >
                <span className="max-lg:hidden">
                  {isDoubleHeader ? 'Doubleheader Tickets' : 'Tickets'}
                </span>
                <span className="lg:hidden">Buy Tickets</span>
              </a>
            </p>
          )}
        <p className={`${COMMON_STYLES.linkText} text-black/50 max-lg:hidden`}>
          <a
            href={
              isAllStarCup
                ? `/all-star-cup/game/${game.gameId}`
                : `/game/${game.gameId}`
            }
          >
            Details
          </a>
        </p>
      </>
    ) : (
      <p className={COMMON_STYLES.linkText}>
        <a
          href={
            isAllStarCup
              ? `/all-star-cup/game/${game.gameId}`
              : `/game/${game.gameId}`
          }
        >
          Game Recap
        </a>
      </p>
    )}
  </div>
);

// Extract game date display component
const GameDate = ({
  isUpcoming,
  today,
  tomorrow,
  formattedLocalTime,
}: GameDateProps) => {
  // Safely parse the date to avoid React errors
  const parsedDate = formattedLocalTime?.date
    ? DateTime.fromFormat(formattedLocalTime.date, 'LLL d, yyyy', {
        locale: 'en-US',
      })
    : null;

  // Only use the parsed date if it's valid
  const isValidDate = parsedDate?.isValid ?? false;

  return (
    <div className="flex h-full flex-col items-center justify-center gap-y-3">
      {isUpcoming && !today && !tomorrow && (
        <p className={COMMON_STYLES.dateText}>
          {isValidDate && parsedDate ? parsedDate.toFormat('EEE') : ''}
        </p>
      )}
      <p className="-mb-3 -mt-2.5 text-[32px] font-black">
        {today
          ? 'Today'
          : tomorrow
            ? 'Tomorrow'
            : isValidDate && parsedDate
              ? parsedDate.toFormat('LLL d')
              : 'TBA'}
      </p>
      {isUpcoming && (
        <p className={COMMON_STYLES.dateText}>
          {formattedLocalTime?.time ?? 'TBA'}
        </p>
      )}
    </div>
  );
};

// Extract game location component
interface GameLocationProps {
  isDoubleHeader: boolean;
  hasGameStatusIssue?: boolean;
  venue: {
    name: string | null;
    venueUrl?: string | null;
    address: {
      city: string | null;
      state: string | null;
      country?: string;
    };
  };
}

const GameLocation = ({
  venue,
  isDoubleHeader,
  hasGameStatusIssue,
}: GameLocationProps) => (
  <div className="order-2 flex h-full flex-col justify-between border-current px-2.5 max-lg:bg-black max-lg:pb-8 max-lg:pt-4 max-lg:text-white lg:border-r">
    <div
      className={clsx(
        'my-auto flex flex-col items-center justify-center',
        isDoubleHeader && 'lg:pt-14',
      )}
    >
      <p className="gap-x-2.5 text-center text-[14px] font-bold max-lg:flex lg:text-[20px] lg:font-black">
        {venue.address?.city && venue.address?.state ? (
          <>
            {venue.address?.city}
            {', '}
            {venue.address?.state}
          </>
        ) : (
          'TBA'
        )}
        <span className="lg:hidden">•</span>
      </p>
      <p className="text-center font-roboto text-[14px] font-bold tracking-[.08em] lg:px-2 lg:font-black">
        {venue?.venueUrl && venue?.name ? (
          <a
            href={
              venue.venueUrl.includes('https://theausl.com')
                ? venue.venueUrl.replace('https://theausl.com', '')
                : venue.venueUrl
            }
            className="underline underline-offset-4"
            target={
              venue.venueUrl.includes('https://theausl.com')
                ? '_self'
                : '_blank'
            }
            rel="noopener noreferrer"
          >
            {venue.name}
          </a>
        ) : venue?.name ? (
          venue.name
        ) : (
          ''
        )}
      </p>
    </div>
    {isDoubleHeader && !hasGameStatusIssue && (
      <p className="text-center font-roboto text-[14px] font-medium tracking-[.08em] max-lg:mt-4 lg:px-2 lg:pb-4">
        SINGLE ADMISSION
        <br />
        DOUBLE-HEADER
      </p>
    )}
  </div>
);

// Extract team display component
interface TeamDisplayProps {
  team?: ITeam;
  score?: string | number;
  isAllStarCup?: boolean;
  isHome?: boolean;
}

const TeamDisplay = ({
  team,
  score,
  isAllStarCup,
  isHome,
}: TeamDisplayProps) => {
  const teamName = team?.name || 'TBD';
  const logo = getLogo(teamName.toLowerCase());
  const colors = getColors(teamName.toLowerCase());
  const teamScore = team?.score;
  const otherScore = isHome ? score : teamScore;

  return (
    <div className="flex flex-col items-center justify-center gap-y-3 max-lg:py-[35px]">
      {isAllStarCup ? (
        <div
          className="relative h-[50px] w-[50px] rounded-full before:absolute before:left-0 before:top-0 before:h-3 before:w-3 before:rounded-full before:bg-[--color]"
          style={
            {
              '--color': colors?.primary,
            } as React.CSSProperties
          }
        >
          <img
            className="h-full w-full rounded-full"
            src={
              team?.players?.find((p) => p.captainFlg)?.imageResource
                ?.imageUrl || '/allstarcup-placeholder.png'
            }
            alt=""
          />
        </div>
      ) : (
        <div
          className={clsx(
            slugify(teamName) === 'blaze' && '!px-[3px]',
            'h-[50px] w-[50px] rounded-full p-2',
          )}
          style={{
            backgroundColor: colors?.primary,
          }}
        >
          {logo && <img className="h-full w-full" src={logo.icon.src} alt="" />}
        </div>
      )}

      <div>
        <p className={COMMON_STYLES.teamName}>{teamName}</p>
      </div>
      {teamScore && (
        <div>
          <p className={COMMON_STYLES.scoreText}>
            {teamScore}
            {Number(teamScore) > Number(otherScore) && (
              <FlagIcon className="w-[10px]" />
            )}
          </p>
        </div>
      )}
    </div>
  );
};

// Extract matchup component
interface GameMatchupProps {
  homeTeam?: ITeam;
  awayTeam?: ITeam;
  isAllStarCup?: boolean;
  gameId: string;
}

interface GameStatusProps {
  game: IGame;
}

const GameMatchup = ({
  homeTeam,
  awayTeam,
  isAllStarCup,
  gameId,
}: GameMatchupProps) => {
  const gameDetailUrl = isAllStarCup
    ? `/all-star-cup/game/${gameId}`
    : `/game/${gameId}`;

  return (
    <div className="relative order-3 flex">
      <a href={gameDetailUrl} className="contents lg:pointer-events-none">
        <div className="flex w-full flex-col items-center justify-center border-r border-current">
          <TeamDisplay
            team={awayTeam}
            score={homeTeam?.score}
            isAllStarCup={isAllStarCup}
          />
        </div>

        <div className="absolute left-1/2 top-1/2 flex h-9 w-9 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-current bg-white">
          <p className="-mb-[5px] -mt-1 text-[14px] font-bold">VS</p>
        </div>

        <div className="flex w-full flex-col items-center justify-center border-current lg:border-r">
          <TeamDisplay
            team={homeTeam}
            score={awayTeam?.score}
            isAllStarCup={isAllStarCup}
            isHome={true}
          />
        </div>
      </a>
    </div>
  );
};

export function ScheduleGame({ item, isLast, isAllStarCup }: GameProps) {
  const isDoubleHeader = Array.isArray(item);

  // Function to check if any game in a doubleheader has been cancelled, postponed, or delayed
  const hasGameStatusIssue = useMemo(() => {
    if (!isDoubleHeader) return false;

    return (item as IGame[]).some(
      (game) =>
        ISSUE_STATUSES.includes(game.recordStatus) ||
        game.cancelledGame !== null,
    );
  }, [isDoubleHeader, item]);

  // If we have a doubleheader with status issues, treat each game separately
  // This will make the component render each game of the doubleheader as a separate row
  const effectiveIsDoubleHeader = isDoubleHeader && !hasGameStatusIssue;

  const getTeam = (competitorId: number | null, color: string | null) => {
    // Check if item is an array first
    const game = isDoubleHeader ? item[0] : item;
    const team = game.competitors?.find(
      (c: ITeam) => c.competitorId === competitorId,
    );
    if (team) {
      return team;
    }
    return getTeamByColor(color ?? '');
  };

  // For single game handling
  const getSingleGameData = (game: IGame) => {
    let homeTeam: ITeam | null = null;
    let awayTeam: ITeam | null = null;

    if (isAllStarCup) {
      // Find home and away teams by matching competitorId with homeTeamId/awayTeamId
      homeTeam = getTeam(game.homeTeamId, game.homeColor);
      awayTeam = getTeam(game.awayTeamId, game.awayColor);
    } else {
      homeTeam =
        game.competitors?.find(
          (c: ITeam) => c.competitorId === game.homeTeamId,
        ) ?? null;
      awayTeam =
        game.competitors?.find(
          (c: ITeam) => c.competitorId === game.awayTeamId,
        ) ?? null;
    }

    // Memoize expensive calculations
    const formattedLocalTime = useMemo(() => {
      if (!game.gameDateIso) return null;
      const result = getLocalDateTime(game.gameDateIso);
      // Ensure we return the correct type
      return typeof result === 'string' ? null : result;
    }, [game.gameDateIso]);

    const today = formattedLocalTime?.date
      ? isToday(new Date(formattedLocalTime.date))
      : false;
    const tomorrow = formattedLocalTime?.date
      ? isTomorrow(new Date(formattedLocalTime.date))
      : false;
    const isUpcoming = formattedLocalTime?.date
      ? isFuture(new Date(formattedLocalTime.date))
      : false;

    return {
      homeTeam,
      awayTeam,
      formattedLocalTime,
      today,
      tomorrow,
      isUpcoming,
    };
  };

  // If we have a doubleheader with status issues, render each game separately
  if (isDoubleHeader && hasGameStatusIssue) {
    return (
      <>
        {(item as IGame[]).map((game, index) => (
          <ScheduleGame
            key={game.gameId}
            item={game}
            isLast={isLast && index === (item as IGame[]).length - 1}
            isAllStarCup={isAllStarCup}
          />
        ))}
      </>
    );
  }

  // Get data for the primary game (first game if double header)
  const primaryGame = isDoubleHeader ? item[0] : item;
  const {
    homeTeam,
    awayTeam,
    formattedLocalTime,
    today,
    tomorrow,
    isUpcoming,
  } = getSingleGameData(primaryGame);

  return (
    <li
      className={clsx(
        'grid border-b border-current uppercase max-lg:rounded max-lg:border lg:grid-cols-[230fr_225fr_340fr_165fr] lg:last:border-b-0 xl:grid-cols-[230fr_225fr_340fr_235fr]',
        effectiveIsDoubleHeader &&
          'lg:h-[334px] lg:grid-cols-[230fr_225fr_505fr] xl:grid-cols-[230fr_225fr_575fr]',
      )}
    >
      {/* date */}
      <div
        className={clsx(
          'relative order-1 flex flex-col items-center justify-center bg-black pb-2.5 pt-8 text-white lg:pb-4 lg:pt-12',
          !isLast &&
            'after:absolute after:-bottom-px after:w-full after:border-b after:border-current max-lg:after:hidden',
        )}
      >
        <GameDate
          gameDate={formattedLocalTime?.date ?? ''}
          isUpcoming={isUpcoming}
          today={today}
          tomorrow={tomorrow}
          formattedLocalTime={formattedLocalTime}
        />

        <GameStatus game={primaryGame} />

        {isUpcoming && (
          <div className="hidden gap-x-6 lg:flex">
            <span data-tooltip-id={`calendar-tooltip-${primaryGame.gameId}`}>
              <AddToCalendarIcon className="w-6 cursor-pointer" />
            </span>
            {primaryGame?.broadcasts?.length > 0 && (
              <a
                href={
                  primaryGame.broadcasts.find(
                    (b: Broadcast) => b.broadcastTypeLk === 'BROAD',
                  )?.link || primaryGame.broadcasts[0]?.link
                }
                target="_blank"
                rel="noreferrer"
                className="flex"
                onClick={() =>
                  trackBroadcastWatch(
                    primaryGame as unknown as GameTrackingData,
                  )
                }
              >
                <EspnIcon className="w-12" />
              </a>
            )}
            {primaryGame?.promotionText && (
              <span
                className="flex items-center gap-1.5 font-roboto text-[11px] font-bold uppercase tracking-[1px]"
                data-tooltip-id={`promo-tooltip-${primaryGame.gameId}`}
              >
                <InfoIcon className="w-4" />
                Promo
              </span>
            )}

            <CalendarTooltip
              id={`calendar-tooltip-${primaryGame.gameId}`}
              game={primaryGame}
            />
            {primaryGame?.promotionText && (
              <PromoTooltip
                id={`promo-tooltip-${primaryGame.gameId}`}
                content={primaryGame?.promotionText ?? ''}
              />
            )}
          </div>
        )}
      </div>

      {/* location */}
      <GameLocation
        venue={primaryGame.venue}
        isDoubleHeader={effectiveIsDoubleHeader}
        hasGameStatusIssue={hasGameStatusIssue}
      />

      {/* matchup */}
      {effectiveIsDoubleHeader ? (
        // Handle double header case
        // Hide secondary game tickets link
        <div className="order-3 grid">
          {item.map((game, i) => {
            const gameData = getSingleGameData(game);
            return (
              <div
                key={i}
                className="grid border-b border-current last:border-0 lg:grid-cols-[340fr_165fr] xl:grid-cols-[340fr_235fr]"
              >
                <GameMatchup
                  key={i}
                  homeTeam={gameData.homeTeam ?? undefined}
                  awayTeam={gameData.awayTeam ?? undefined}
                  isAllStarCup={isAllStarCup}
                  gameId={game.gameId.toString()}
                />
                {/* Hide secondary game tickets link, but keep the game actions */}
                {i === 0 ? (
                  <GameActions
                    isUpcoming={isUpcoming}
                    game={game}
                    isAllStarCup={isAllStarCup}
                    isDoubleHeader={effectiveIsDoubleHeader}
                    hideTicketsLink={hasGameStatusIssue}
                  />
                ) : (
                  <GameActions
                    isUpcoming={isUpcoming}
                    game={game}
                    isAllStarCup={isAllStarCup}
                    isDoubleHeader={effectiveIsDoubleHeader}
                    hideTicketsLink={true}
                  />
                )}
              </div>
            );
          })}
        </div>
      ) : (
        // Handle single game case
        <GameMatchup
          homeTeam={homeTeam ?? undefined}
          awayTeam={awayTeam ?? undefined}
          isAllStarCup={isAllStarCup}
          gameId={primaryGame.gameId.toString()}
        />
      )}

      {/* tickets and details */}
      {!effectiveIsDoubleHeader && (
        <GameActions
          isUpcoming={isUpcoming}
          game={primaryGame}
          isAllStarCup={isAllStarCup}
        />
      )}
    </li>
  );
}
